import { parrotApi } from '@libs/openapi';
import { useEffect, useState } from 'react';
import { useDebounce } from 'react-use';
import type { BestSwapResult } from 'src/types';
import { Decimal } from 'parrot-sui-sdk';

export const useParrotBestRoutes = (
  coinTypeA: string | undefined,
  coinTypeB: string | undefined,
  amount: string | undefined,
  exactIn: boolean,
) => {
  const [bestRoute, setBestRoute] = useState<BestSwapResult>();
  const [notLiquidity, setNotLiquidity] = useState(false);
  const [notRoute, setNotRoute] = useState(false);
  const [loading, setLoading] = useState(false);

  const getBestRoutes = async (isReload: boolean = false) => {
    if (isReload) {
      setNotRoute(false);
      setLoading(true);
    }

    if (
      coinTypeA === undefined ||
      coinTypeB === undefined ||
      amount === undefined ||
      amount === '0'
    ) {
      setBestRoute(undefined);
      setLoading(false);
      return;
    }

    try {
      const swapPayload = await parrotApi.get('/clmm/trading/best-route', {
        query: {
          from: coinTypeA,
          to: coinTypeB,
          amount: amount,
          by_amount_in: exactIn,
        },
      });
      if (!swapPayload.data || swapPayload.error) {
        setBestRoute(undefined);
        setLoading(false);
        setNotRoute(true);
        return;
      }

      setBestRoute({
        ...swapPayload.data,
        by_amount_in: exactIn,
      });

      if (
        (exactIn && new Decimal(swapPayload.data.amount_in).lt(amount)) ||
        (!exactIn && new Decimal(swapPayload.data.amount_out).lt(amount)) ||
        new Decimal(swapPayload.data.amount_in).eq(0) ||
        new Decimal(swapPayload.data.amount_out).eq(0)
      ) {
        setNotLiquidity(true);
      } else {
        setNotLiquidity(false);
      }
    } catch (err) {
      setNotRoute(true);
    }

    setLoading(false);
  };

  useDebounce(
    () => {
      getBestRoutes(true);
    },
    300,
    [coinTypeA, coinTypeB, amount, exactIn],
  );

  useEffect(() => {
    let timer: NodeJS.Timeout | undefined;
    const loop = () => {
      timer = setTimeout(() => {
        getBestRoutes();
        loop();
      }, 15000);
    };
    loop();
    return () => clearInterval(timer);
  }, [coinTypeA, coinTypeB, amount, exactIn]);

  return {
    bestRoute,
    loading,
    notLiquidity,
    notRoute,
  };
};
